// Standalone tennis bet-product screen (one per registry entry, mirrors the
// football per-product pages like PowerballPage). Renders the product's
// suggested acca / picks to stake — honest-empty until the TIM engine's data
// flows (no fabricated slips), tier-gated, with stake CTAs.
import { Link } from "react-router-dom";
import { useUserTier } from "../../../auth/useUserTier.ts";
import { tierMeets } from "../../../auth/tier-gates.ts";
import { Card, Badge, Kicker, AwaitingPanel, StatusPill, TennisHeader, Stack, daysUntil, R1_DATE } from "../_kit.tsx";
import { productBySlug, type TennisProduct, type ProductFormat } from "./registry.ts";
import { useTennisProduct, type TennisPick, type UseTennisProductState } from "./useTennisProduct.ts";
import { TennisPickList } from "./TennisPickList.tsx";

const OUTPUT_NOUN: Record<ProductFormat, string> = {
  acca: "suggested acca",
  board: "ranked picks board",
  card: "5-market card",
  tiers: "tiered picks",
  single: "rolling single",
  alert: "live alerts",
};

function fmtOdds(o: number | null): string {
  return o != null ? o.toFixed(2) : "—";
}

// Flagship clay-court hero for a single product — mirrors the Signals/Dashboard
// hero so every product screen reads as premium, not a plain header.
function ProductHero({ p, product, live, unlocked }: { p: TennisProduct; product: UseTennisProductState; live: boolean; unlocked: boolean }): JSX.Element {
  const round1 = daysUntil(R1_DATE);
  const r1Label = round1 === 0 ? "Live now" : round1 === 1 ? "Tomorrow" : `in ${round1} days`;
  const hasPicks = product.found && product.picks.length > 0;
  const combined = product.headline?.combined_odds ?? null;
  return (
    <section className="tn-hero">
      <div className="tn-hero__main">
        <Kicker green>{p.tagline}</Kicker>
        <h1 className="tn-hero__statement">{p.name}</h1>
        <p className="tn-hero__sub">{p.what}</p>
        <div className="tn-hero__chips">
          <Badge tone={p.family === "volume" ? "tennis" : "info"}>{p.family === "volume" ? "Volume" : "Layer"}</Badge>
          {/* Tier chip is an upsell — only show it when the viewer doesn't already have access. */}
          {unlocked ? null : <Badge tone="muted">{p.tierLabel}</Badge>}
          <StatusPill live={live}>{live ? "Pilot · live" : "Pilot · pre-tournament"}</StatusPill>
        </div>
      </div>
      <aside className="tn-hero__aside">
        <dl className="tn-def">
          <div className="tn-def__row">
            <dt className="tn-def__k">Output</dt>
            <dd className="tn-def__v">{OUTPUT_NOUN[p.format]}</dd>
          </div>
          <div className="tn-def__row">
            <dt className="tn-def__k">Access</dt>
            <dd className={`tn-def__v${unlocked ? " tn-def__v--green" : ""}`}>{unlocked ? "Included" : p.tierLabel}</dd>
          </div>
          {hasPicks ? (
            <div className="tn-def__row">
              <dt className="tn-def__k">Picks today</dt>
              <dd className="tn-def__v tn-def__v--green">{product.picks.length}</dd>
            </div>
          ) : (
            <div className="tn-def__row">
              <dt className="tn-def__k">Round 1</dt>
              <dd className="tn-def__v tn-def__v--green">{r1Label}</dd>
            </div>
          )}
          {p.format === "acca" && combined !== null ? (
            <div className="tn-def__row">
              <dt className="tn-def__k">Combined odds</dt>
              <dd className="tn-def__v tn-def__v--green">{fmtOdds(combined)}</dd>
            </div>
          ) : (
            <div className="tn-def__row">
              <dt className="tn-def__k">Surface</dt>
              <dd className="tn-def__v tn-def__v--green">Clay</dd>
            </div>
          )}
        </dl>
      </aside>
    </section>
  );
}

function StakeSurface({
  p,
  picks,
  combinedOdds,
  tournament,
}: {
  p: TennisProduct;
  picks: TennisPick[];
  combinedOdds: number | null;
  tournament: string | null;
}): JSX.Element {
  const isAcca = p.format === "acca";
  return (
    <section className="tn-acca">
      <div className="tn-acca__head">
        <div>
          <Kicker green>Today&rsquo;s {OUTPUT_NOUN[p.format]}</Kicker>
          <div className="tn-acca__title">{p.name} · {tournament || "French Open 2026"}</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 6, maxWidth: "56ch", lineHeight: 1.55 }}>{p.what}</div>
        </div>
        {isAcca ? (
          <div className="tn-acca__odds">
            <div className="tn-acca__odds-v">{fmtOdds(combinedOdds)}</div>
            <span className="tn-await__count-l">combined odds</span>
          </div>
        ) : null}
      </div>
      <TennisPickList picks={picks} />
      <div className="tn-acca__actions">
        <span className="tn-cta tn-cta--ghost">Copy Slip</span>
        <span className="tn-cta tn-cta--ghost">Stake on Stake</span>
      </div>
    </section>
  );
}

export function TennisProductScreen({ slug }: { slug: string }): JSX.Element {
  const { tierCode } = useUserTier();
  const p = productBySlug(slug);
  const product = useTennisProduct(slug);
  const live = daysUntil(R1_DATE) === 0;

  if (!p) {
    return (
      <div className="tn">
        <TennisHeader eyebrow="Tennis · Products" title="Unknown product" meta={null} />
        <Card lead="That product doesn't exist." footer={<Link to="/exchange/tennis/products" className="tn-cta">← All products</Link>} />
      </div>
    );
  }

  const unlocked = tierMeets(tierCode, p.tier);
  return (
    <div className="tn">
      <div className="tn-backrow">
        <Link to="/exchange/tennis/dashboard" className="tn-cta tn-cta--ghost">← Tennis Dashboard</Link>
      </div>

      <ProductHero p={p} product={product} live={live} unlocked={unlocked} />

      <Stack>
      {unlocked ? (
        product.loading ? (
          <Card eyebrow="Loading" title="Fetching today's picks">
            <AwaitingPanel eyebrow="Reading the pool" title="Loading published picks…" body="Pulling this product's latest output from the intelligence pool." ghosts={3} />
          </Card>
        ) : product.found && product.picks.length > 0 ? (
          <StakeSurface
            p={p}
            picks={product.picks}
            combinedOdds={product.headline?.combined_odds ?? null}
            tournament={product.tournament}
          />
        ) : p.format === "alert" ? (
          <Card eyebrow="Live product" title="Fires during matches">
            <AwaitingPanel
              eyebrow="In-play, not pre-match"
              title="This surface fires live"
              body="It triggers the moment a match reaches its decisive phase — the seconds before the live book adjusts. There's nothing to show before play begins; it populates in-play, never padded with pre-match guesses."
              ghosts={0}
            />
          </Card>
        ) : p.family === "layer" ? (
          <Card eyebrow="Awaiting intelligence inputs" title="Builds when its signal fires">
            <AwaitingPanel
              eyebrow="No fabricated picks"
              title="This layer needs its own signal, not just a price"
              body="The draw is priced — but this layer reads a specific player-condition signal (clay readiness, draw path, surface fatigue or head-to-head history) on top of those prices. It surfaces picks only when that signal genuinely fires for a live match; until the condition data is in, it stays honestly empty, never padded."
              ghosts={0}
            />
          </Card>
        ) : (
          <Card eyebrow="Awaiting data" title="Fills on a permitting day">
            <AwaitingPanel
              eyebrow="No fabricated picks"
              title="This product stays empty until real edges exist"
              body="Every suggested pick comes from a genuine qualifying BET in the day's priced board. On a day that produces enough edges this fills automatically; today it's honestly empty, never padded."
              ghosts={0}
            />
          </Card>
        )
      ) : (
        <Card>
          <div className="tn-lock">
            <div className="tn-lock__title">{p.tierLabel} feature</div>
            <div className="tn-lock__body">{p.name} and its suggested picks are {p.tierLabel.replace("+", "")} tier and above.</div>
            <Link to="/pricing" className="tn-cta">See plans →</Link>
          </div>
        </Card>
      )}
      </Stack>
    </div>
  );
}
